import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, Heart, MessageCircle, Crown, Settings, Shield,
  TrendingUp, Activity, BarChart3, ChevronRight, RefreshCw,
  UserCheck, Sparkles, Target, Zap, AlertTriangle, Clock,
  ArrowUpRight, ArrowDownRight, MessageSquare, Unlock,
  Calendar, DollarSign, Award, Leaf, Timer, CheckCircle2,
  XCircle, Hourglass, Send, Eye, ThumbsUp, Video,
  Home, Filter, Flag, UserPlus, Bell, Radio, FileText, Ticket,
  Search, Download, Mail, UserCog, Database, Server,
  Smartphone, Monitor, HelpCircle, Sun, Moon, Sunrise, Share2
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area, Legend
} from "recharts";
import { format } from "date-fns";
import AdminMarketing from "./AdminMarketing";
import AdminCoins from "./AdminCoins";
import AdminMatchmaker from "./AdminMatchmaker";
import AdminLayout from "@/components/AdminLayout";

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "Good morning", icon: Sunrise };
  if (hour < 17) return { text: "Good afternoon", icon: Sun };
  return { text: "Good evening", icon: Moon };
};

// Get role display info
const getRoleInfo = (role, isSuperAdmin) => {
  if (isSuperAdmin) return { label: "Super Admin", color: "bg-amber-500", icon: Crown };
  const roles = {
    admin: { label: "Admin", color: "bg-indigo-500", icon: Shield },
    moderator: { label: "Moderator", color: "bg-purple-500", icon: Shield },
    support: { label: "Support", color: "bg-blue-500", icon: MessageSquare },
    analyst: { label: "Analyst", color: "bg-green-500", icon: Eye },
    marketing: { label: "Marketing", color: "bg-pink-500", icon: Share2 },
    matchmaker: { label: "Matchmaker", color: "bg-rose-500", icon: Heart },
  };
  return roles[role] || { label: role || "Admin", color: "bg-gray-500", icon: Shield };
};

// Role-based quick actions
const getQuickActionsForRole = (role, isSuperAdmin, overview) => {
  const allActions = {
    team: { title: "Admin Team", desc: "Manage admins", icon: Users, link: "/admin/team", color: "bg-teal-500" },
    infrastructure: { title: "Infrastructure", desc: "System overview", icon: Server, link: "/admin/infrastructure", color: "bg-slate-700" },
    security: { title: "Security", desc: "Monitor threats", icon: Shield, link: "/admin/security", color: "bg-red-600" },
    users: { title: "User Management", desc: "View & manage users", icon: Users, link: "/admin/users", color: "bg-[#1C3A7A]" },
    reports: { title: "Reports", desc: "User reports", icon: AlertTriangle, link: "/admin/reports", color: "bg-red-500", badge: overview?.moderation?.pending_reports },
    verifications: { title: "Verifications", desc: "ID reviews", icon: Shield, link: "/admin/verifications", color: "bg-purple-500", badge: overview?.moderation?.pending_verifications },
    support: { title: "Support Tickets", desc: "Handle requests", icon: MessageSquare, link: "/admin/support", color: "bg-blue-500" },
    analytics: { title: "Analytics", desc: "View metrics", icon: BarChart3, link: "/admin", color: "bg-green-500" },
    testerInvites: { title: "Tester Invites", desc: "Send beta emails", icon: Smartphone, link: "/admin/tester-invites", color: "bg-[#3DDC84]" },
    subscriptions: { title: "Subscriptions", desc: "Revenue insights", icon: Crown, link: "/admin/subscriptions", color: "bg-[#FAB710]" },
    settings: { title: "Settings", desc: "App config", icon: Settings, link: "/admin/settings", color: "bg-gray-500" },
  };

  if (isSuperAdmin) {
    return [allActions.team, allActions.infrastructure, allActions.security, allActions.users, allActions.reports, allActions.settings];
  }

  const roleActions = {
    admin: [allActions.users, allActions.reports, allActions.support, allActions.analytics],
    moderator: [allActions.reports, allActions.verifications, allActions.analytics],
    support: [allActions.support, allActions.analytics],
    analyst: [allActions.analytics, allActions.subscriptions],
    marketing: [allActions.testerInvites, allActions.analytics],
    matchmaker: [allActions.analytics],
  };

  return roleActions[role] || [allActions.analytics, allActions.settings];
};

const COLORS = {
  primary: "#1C3A7A",
  gold: "#FAB710",
  green: "#22C55E",
  red: "#EF4444",
  purple: "#8B5CF6",
  blue: "#3B82F6",
  orange: "#F97316",
  teal: "#14B8A6",
  pink: "#EC4899"
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [matching, setMatching] = useState(null);
  const [matchingTrends, setMatchingTrends] = useState(null);
  const [userJourney, setUserJourney] = useState(null);
  const [guidedMessaging, setGuidedMessaging] = useState(null);
  const [dailyEngagement, setDailyEngagement] = useState(null);
  const [subscriptionFunnel, setSubscriptionFunnel] = useState(null);
  const [recoveryInsights, setRecoveryInsights] = useState(null);
  const [successMetrics, setSuccessMetrics] = useState(null);
  const [onboardingDropoff, setOnboardingDropoff] = useState(null);
  const [referralProgram, setReferralProgram] = useState(null);
  const [sponsorSupport, setSponsorSupport] = useState(null);
  const [filtersUsage, setFiltersUsage] = useState(null);
  const [safetyModeration, setSafetyModeration] = useState(null);
  const [activityFeed, setActivityFeed] = useState(null);
  const [aarrrMetrics, setAarrrMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [quickSearch, setQuickSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [dateRange, setDateRange] = useState(14);
  
  // Get admin user from localStorage
  const [adminUser, setAdminUser] = useState(null);
  
  useEffect(() => {
    const storedAdmin = localStorage.getItem("recoverymatch_admin_user");
    if (storedAdmin) {
      try {
        setAdminUser(JSON.parse(storedAdmin));
      } catch (e) {
        console.error("Failed to parse admin user", e);
      }
    }
  }, []);

  const dateRanges = [
    { label: "7D", value: 7 },
    { label: "14D", value: 14 },
    { label: "30D", value: 30 },
    { label: "90D", value: 90 }
  ];

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  // Auto-refresh effect
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchAllData(true);
      }, 30000); // 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, dateRange]);

  const fetchAllData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.getAdminOverview(),
        api.getAdminOnboardingAnalytics(),
        api.getAdminMatchingAnalytics(),
        api.getAdminMatchingTrends(dateRange),
        api.getAdminUserJourney(),
        api.getAdminGuidedMessaging(),
        api.getAdminDailyEngagement(dateRange),
        api.getAdminSubscriptionFunnel(),
        api.getAdminRecoveryInsights(),
        api.getAdminSuccessMetrics(),
        api.getAdminOnboardingDropoff(),
        api.getAdminReferralProgram(),
        api.getAdminSponsorSupport(),
        api.getAdminFiltersUsage(),
        api.getAdminSafetyModeration(),
        api.getAdminActivityFeed(),
        api.getAdminAARRRMetrics()
      ]);
      // Resilient: one failed call no longer blanks the whole dashboard
      const d = (i) => (results[i].status === "fulfilled" ? results[i].value.data : null);
      const setters = [
        setOverview, setOnboarding, setMatching, setMatchingTrends, setUserJourney,
        setGuidedMessaging, setDailyEngagement, setSubscriptionFunnel, setRecoveryInsights,
        setSuccessMetrics, setOnboardingDropoff, setReferralProgram, setSponsorSupport,
        setFiltersUsage, setSafetyModeration, setActivityFeed, setAarrrMetrics
      ];
      setters.forEach((setter, i) => {
        const data = d(i);
        if (data) setter(data);
      });
      const failedCount = results.filter((r) => r.status === "rejected").length;
      if (failedCount > 0 && !silent) {
        toast.warning(`${failedCount} dashboard sections failed to load — hit Refresh to retry`, { duration: 10000 });
      }
    } catch (error) {
      console.error("Failed to load admin data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  // Quick Search functionality
  const handleQuickSearch = async (query) => {
    setQuickSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    try {
      const response = await api.searchUsers(query);
      setSearchResults(response.data.users?.slice(0, 5) || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Export data functionality
  const handleExportData = async (type) => {
    setExporting(true);
    try {
      let data = [];
      let filename = '';
      
      if (type === 'users') {
        const response = await api.getAdminUsers({ limit: 1000 });
        data = response.data.users || [];
        filename = `recoverymatch_users_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      } else if (type === 'matches') {
        data = [
          { metric: 'Total Matches', value: overview?.matching?.total_matches || 0 },
          { metric: 'Mutual Matches', value: overview?.matching?.mutual_matches || 0 },
          { metric: 'Match Rate', value: `${overview?.matching?.match_rate || 0}%` },
          { metric: 'Average Compatibility', value: overview?.matching?.avg_compatibility || 0 },
        ];
        filename = `recoverymatch_matches_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      } else if (type === 'analytics') {
        data = [
          { metric: 'Total Users', value: overview?.users?.total || 0 },
          { metric: 'Active Users', value: overview?.users?.active || 0 },
          { metric: 'Paying Subscribers', value: (overview?.subscriptions?.premium || 0) + (overview?.subscriptions?.elite || 0) },
          { metric: 'Conversations', value: overview?.messaging?.conversations || 0 },
          { metric: 'Messages Sent', value: overview?.messaging?.messages || 0 },
        ];
        filename = `recoverymatch_analytics_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      }
      
      // Convert to CSV
      if (data.length > 0) {
        const headers = Object.keys(data[0]);
        const csvContent = [
          headers.join(','),
          ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${type} data`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
    );
  }

  // Tab configuration (perm = hidden unless admin has it; UX only)
  const tabs = [
    { id: "aarrr", label: "AARRR Metrics", icon: TrendingUp },
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "matchmaker", label: "Matchmaker", icon: Heart, perm: "matchmaking.tools" },
    { id: "activity", label: "Live Feed", icon: Radio },
    { id: "onboarding", label: "Onboarding", icon: UserCheck },
    { id: "messaging", label: "Messaging", icon: MessageSquare },
    { id: "engagement", label: "Engagement", icon: Eye },
    { id: "subscriptions", label: "Subscriptions", icon: Crown },
    { id: "marketing", label: "Marketing", icon: Ticket },
    { id: "coins", label: "Coins & Gifts", icon: Award },
    { id: "sponsor", label: "Sponsor & Support", icon: Home },
    { id: "filters", label: "Filters Usage", icon: Filter },
    { id: "safety", label: "Safety", icon: Shield },
    { id: "recovery", label: "Recovery Insights", icon: Leaf },
    { id: "success", label: "Success Metrics", icon: Award },
    { id: "referral", label: "Referral Program", icon: Users },
  ];

  // Get personalized info
  const greeting = getGreeting();
  const roleInfo = adminUser ? getRoleInfo(adminUser.role, adminUser.is_super_admin) : null;
  const roleQuickActions = adminUser ? getQuickActionsForRole(adminUser.role, adminUser.is_super_admin, overview) : [];
  const GreetingIcon = greeting.icon;
  const RoleIcon = roleInfo?.icon || Shield;

  return (
    <AdminLayout>
    <div className="bg-gray-50" data-testid="admin-dashboard">
      {/* Personalized Welcome Header */}
      <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] text-white px-6 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Personalized Greeting */}
          {adminUser && (
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-5 border-b border-white/20">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <GreetingIcon className="w-7 h-7 text-[#FAB710]" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    {greeting.text}, {adminUser.name?.split(' ')[0] || 'Admin'}!
                  </h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${roleInfo?.color} text-white`}>
                      <RoleIcon className="w-3 h-3" />
                      {roleInfo?.label}
                    </span>
                    <span className="text-white/60 text-sm">
                      {format(new Date(), "EEEE, MMMM d, yyyy")}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Role-based Quick Actions */}
              <div className="flex flex-wrap items-center gap-2">
                {roleQuickActions.slice(0, 4).map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => navigate(action.link)}
                    className="relative flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2.5 rounded-xl transition-all group"
                  >
                    <div className={`w-8 h-8 ${action.color} rounded-lg flex items-center justify-center`}>
                      <action.icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">{action.title}</p>
                      <p className="text-xs text-white/60">{action.desc}</p>
                    </div>
                    {action.badge > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                        {action.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="min-w-0">
              <h2 className="text-xl font-bold">Dashboard Analytics</h2>
              <p className="text-white/70 mt-1">RecoveryMatch Comprehensive Overview</p>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              {/* Quick Search */}
              <div className="relative">
                <div className="flex items-center bg-white/10 rounded-xl px-3 py-2">
                  <Search className="w-4 h-4 text-white/70 mr-2" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={quickSearch}
                    onChange={(e) => handleQuickSearch(e.target.value)}
                    onFocus={() => quickSearch.length >= 2 && setShowSearchResults(true)}
                    className="bg-transparent text-white placeholder-white/50 outline-none w-40"
                    data-testid="quick-search-input"
                  />
                </div>
                {/* Search Results Dropdown */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg shadow-xl z-50 overflow-hidden">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => {
                          navigate(`/admin/users?search=${user.email}`);
                          setShowSearchResults(false);
                          setQuickSearch('');
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left border-b last:border-0"
                      >
                        <img 
                          src={user.photos?.[0] || `https://ui-avatars.com/api/?name=${user.name}`} 
                          className="w-8 h-8 rounded-full object-cover"
                          alt=""
                        />
                        <div>
                          <p className="text-gray-900 font-medium text-sm">{user.name}</p>
                          <p className="text-gray-500 text-xs">{user.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Export Button */}
              <div className="relative group">
                <button
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-colors"
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl z-50 overflow-hidden hidden group-hover:block min-w-[160px]">
                  <button
                    onClick={() => handleExportData('users')}
                    disabled={exporting}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm"
                  >
                    <Users className="w-4 h-4" /> Export Users
                  </button>
                  <button
                    onClick={() => handleExportData('matches')}
                    disabled={exporting}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm"
                  >
                    <Heart className="w-4 h-4" /> Export Matches
                  </button>
                  <button
                    onClick={() => handleExportData('analytics')}
                    disabled={exporting}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm"
                  >
                    <BarChart3 className="w-4 h-4" /> Export Analytics
                  </button>
                </div>
              </div>
              
              {/* Date Range Selector */}
              <div className="flex gap-1 bg-white/10 p-1 rounded-xl">
                {dateRanges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setDateRange(range.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      dateRange === range.value
                        ? 'bg-white text-[#1C3A7A]'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>

              {/* Auto-Refresh Toggle */}
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  autoRefresh 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
                data-testid="auto-refresh-btn"
              >
                <Clock className={`w-4 h-4 ${autoRefresh ? 'animate-pulse' : ''}`} />
                {autoRefresh ? 'Live' : 'Auto'}
              </button>
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard 
              title="Total Users"
              value={overview?.users.total || 0}
              subtext={`+${overview?.users.today || 0} today`}
              icon={Users}
              trend="up"
            />
            <MetricCard 
              title="Mutual Matches"
              value={overview?.matching.mutual_matches || 0}
              subtext={`${overview?.matching.match_rate || 0}% rate`}
              icon={Heart}
              trend="up"
            />
            <MetricCard 
              title="Conversations"
              value={overview?.messaging.conversations || 0}
              subtext={`${overview?.messaging.messages || 0} messages`}
              icon={MessageCircle}
            />
            <MetricCard 
              title="Paying Subscribers"
              value={(overview?.subscriptions.premium || 0) + (overview?.subscriptions.elite || 0)}
              subtext={`${overview?.subscriptions.elite || 0} Elite · ${overview?.subscriptions.comped || 0} comped excluded`}
              icon={Crown}
              highlight
            />
          </div>
        </div>
      </div>

      {/* Tab Navigation — wraps onto multiple rows; no clipped labels */}
      <div className="bg-white border-b sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-1 py-2">
            {tabs.filter((t) => !t.perm || adminUser?.is_super_admin || (adminUser?.permissions || []).includes(t.perm)).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-[#1C3A7A] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 shrink-0" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* AARRR Metrics Tab */}
        {activeTab === "aarrr" && (
          <AARRRTab aarrrMetrics={aarrrMetrics} />
        )}

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <OverviewTab 
            userJourney={userJourney}
            matching={matching}
            matchingTrends={matchingTrends}
            overview={overview}
            navigate={navigate}
          />
        )}

        {/* Matchmaker Tab */}
        {activeTab === "matchmaker" && (
          <AdminMatchmaker />
        )}

        {/* Onboarding Tab */}
        {activeTab === "onboarding" && (
          <OnboardingTab 
            onboarding={onboarding}
            onboardingDropoff={onboardingDropoff}
          />
        )}

        {/* Messaging Tab */}
        {activeTab === "messaging" && (
          <MessagingTab guidedMessaging={guidedMessaging} />
        )}

        {/* Engagement Tab */}
        {activeTab === "engagement" && (
          <EngagementTab dailyEngagement={dailyEngagement} />
        )}

        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <SubscriptionsTab subscriptionFunnel={subscriptionFunnel} />
        )}

        {/* Marketing Tab */}
        {activeTab === "marketing" && (
          <AdminMarketing />
        )}

        {/* Coins & Gifts Tab */}
        {activeTab === "coins" && (
          <AdminCoins />
        )}

        {/* Sponsor & Support Tab */}
        {activeTab === "sponsor" && (
          <SponsorSupportTab sponsorSupport={sponsorSupport} />
        )}

        {/* Filters Usage Tab */}
        {activeTab === "filters" && (
          <FiltersUsageTab filtersUsage={filtersUsage} />
        )}

        {/* Safety & Moderation Tab */}
        {activeTab === "safety" && (
          <SafetyModerationTab safetyModeration={safetyModeration} navigate={navigate} />
        )}

        {/* Activity Feed Tab */}
        {activeTab === "activity" && (
          <ActivityFeedTab activityFeed={activityFeed} />
        )}

        {/* Recovery Insights Tab */}
        {activeTab === "recovery" && (
          <RecoveryTab recoveryInsights={recoveryInsights} />
        )}

        {/* Success Metrics Tab */}
        {activeTab === "success" && (
          <SuccessTab successMetrics={successMetrics} />
        )}

        {/* Referral Program Tab */}
        {activeTab === "referral" && (
          <ReferralTab referralProgram={referralProgram} />
        )}
      </div>
    </div>
    </AdminLayout>
  );
};


// ==================== OVERVIEW TAB ====================
const OverviewTab = ({ userJourney, matching, matchingTrends, overview, navigate }) => {
  const qualityData = matching ? [
    { name: "Exceptional", value: matching.quality_distribution.exceptional, color: COLORS.green },
    { name: "Strong", value: matching.quality_distribution.strong, color: COLORS.blue },
    { name: "Good", value: matching.quality_distribution.good, color: COLORS.gold }
  ] : [];

  const statusData = matching ? [
    { name: "Pending", value: matching.status_distribution.pending, color: COLORS.purple },
    { name: "Interested", value: matching.status_distribution.interested, color: COLORS.gold },
    { name: "Mutual", value: matching.status_distribution.matched, color: COLORS.green },
    { name: "Passed", value: matching.status_distribution.not_aligned, color: COLORS.red },
    { name: "Expired", value: matching.status_distribution.expired, color: "#9CA3AF" }
  ] : [];

  const quickLinks = [
    { title: "User Management", desc: "View & manage users", icon: Users, link: "/admin/users", color: "bg-[#1C3A7A]" },
    { title: "Messaging Analytics", desc: "Conversations & prompts", icon: MessageCircle, link: "/admin/messaging", color: "bg-blue-500" },
    { title: "Subscriptions", desc: "Revenue insights", icon: Crown, link: "/admin/subscriptions", color: "bg-[#FAB710]" },
    { title: "Reports", desc: "User reports", icon: AlertTriangle, link: "/admin/reports", color: "bg-red-500", badge: overview?.moderation.pending_reports },
    { title: "Community", desc: "Feed moderation", icon: MessageCircle, link: "/admin/community", color: "bg-emerald-600" },
    { title: "Verifications", desc: "ID reviews", icon: Shield, link: "/admin/verifications", color: "bg-purple-500", badge: overview?.moderation.pending_verifications },
    { title: "Admin Team", desc: "Manage admins", icon: Users, link: "/admin/team", color: "bg-teal-500" },
    { title: "Infrastructure", desc: "System overview", icon: Server, link: "/admin/infrastructure", color: "bg-slate-700" },
    { title: "Tester Invites", desc: "Android beta emails", icon: Smartphone, link: "/admin/tester-invites", color: "bg-[#3DDC84]" },
    { title: "Settings", desc: "App config", icon: Settings, link: "/admin/settings", color: "bg-gray-500" },
    { title: "Documentation", desc: "Technical docs", icon: FileText, link: "/admin/docs", color: "bg-indigo-500" }
  ];

  return (
    <>
      {/* User Journey Funnel */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#1C3A7A]" />
            User Journey Funnel
          </h2>
          <span className="text-sm text-gray-500">{userJourney?.total_users || 0} total users</span>
        </div>
        <div className="grid grid-cols-6 gap-2">
          {userJourney?.funnel.map((stage, idx) => (
            <div key={idx} className="text-center">
              <div 
                className="h-24 rounded-lg flex items-end justify-center mb-2 transition-all"
                style={{ 
                  backgroundColor: `rgba(28, 58, 122, ${0.2 + (stage.pct / 100) * 0.8})`,
                  height: `${Math.max(40, stage.pct)}px`
                }}
              >
                <span className="text-white font-bold text-sm pb-1">{stage.pct}%</span>
              </div>
              <p className="text-xs font-medium text-gray-700">{stage.stage}</p>
              <p className="text-xs text-gray-400">{stage.count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Signup Platform Breakdown */}
      {overview?.signup_platforms && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#1C3A7A]" />
            Signup Platform Breakdown
          </h2>
          <div className="grid grid-cols-4 gap-4">
            {/* iOS */}
            <div className="relative group">
              <div className="text-center p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 hover:shadow-md transition-all">
                <div className="w-14 h-14 mx-auto mb-3 bg-black rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{overview.signup_platforms.ios || 0}</p>
                <p className="text-sm font-medium text-gray-500 mt-1">iOS</p>
              </div>
            </div>
            
            {/* Android */}
            <div className="relative group">
              <div className="text-center p-5 bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl border border-green-200 hover:shadow-md transition-all">
                <div className="w-14 h-14 mx-auto mb-3 bg-[#3DDC84] rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{overview.signup_platforms.android || 0}</p>
                <p className="text-sm font-medium text-gray-500 mt-1">Android</p>
              </div>
            </div>
            
            {/* Web */}
            <div className="relative group">
              <div className="text-center p-5 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl border border-blue-200 hover:shadow-md transition-all">
                <div className="w-14 h-14 mx-auto mb-3 bg-[#1C3A7A] rounded-2xl flex items-center justify-center shadow-lg">
                  <Monitor className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{overview.signup_platforms.web || 0}</p>
                <p className="text-sm font-medium text-gray-500 mt-1">Web</p>
              </div>
            </div>
            
            {/* Unknown/Legacy */}
            <div className="relative group">
              <div className="text-center p-5 bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl border border-gray-200 hover:shadow-md transition-all">
                <div className="w-14 h-14 mx-auto mb-3 bg-gray-400 rounded-2xl flex items-center justify-center shadow-lg">
                  <HelpCircle className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{overview.signup_platforms.unknown || 0}</p>
                <p className="text-sm font-medium text-gray-500 mt-1">Legacy</p>
              </div>
            </div>
          </div>
          
          {/* Mobile vs Web Bar */}
          <div className="mt-6 p-4 bg-gradient-to-r from-[#1C3A7A]/5 to-[#FAB710]/5 rounded-xl border border-[#1C3A7A]/10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">Mobile vs Web Signups</span>
              <span className="text-lg font-bold text-[#1C3A7A]">
                {Math.round(((overview.signup_platforms.ios + overview.signup_platforms.android) / 
                  (overview.signup_platforms.ios + overview.signup_platforms.android + overview.signup_platforms.web || 1)) * 100)}% Mobile
              </span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#1C3A7A] to-[#FAB710] rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.round(((overview.signup_platforms.ios + overview.signup_platforms.android) / 
                    (overview.signup_platforms.ios + overview.signup_platforms.android + overview.signup_platforms.web || 1)) * 100)}%` 
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Matching & Status Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Match Quality Distribution */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FAB710]" />
            Match Quality Distribution
          </h2>
          <div className="h-48 min-h-[192px]">
            <ResponsiveContainer width="100%" height="100%" minHeight={192}>
              <PieChart>
                <Pie
                  data={qualityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {qualityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {qualityData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-xl">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Avg Compatibility Score</span>
              <span className="font-bold text-[#1C3A7A]">{matching?.averages.compatibility_score || 0}%</span>
            </div>
          </div>
        </div>

        {/* Match Status Breakdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#1C3A7A]" />
            Match Action Breakdown
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" fontSize={12} />
                <YAxis dataKey="name" type="category" fontSize={12} width={70} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-green-50 rounded-lg">
              <p className="text-lg font-bold text-green-600">{matching?.rates.acceptance_rate || 0}%</p>
              <p className="text-xs text-gray-500">Acceptance</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <p className="text-lg font-bold text-blue-600">{matching?.rates.mutual_rate || 0}%</p>
              <p className="text-xs text-gray-500">Mutual Rate</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <p className="text-lg font-bold text-gray-600">{matching?.rates.expiry_rate || 0}%</p>
              <p className="text-xs text-gray-500">Expiry Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Match Generation Trends */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#1C3A7A]" />
          Match Generation Trends (14 days)
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={matchingTrends?.trends || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(d) => format(new Date(d), "MM/dd")}
                fontSize={12}
              />
              <YAxis fontSize={12} />
              <Tooltip labelFormatter={(d) => format(new Date(d), "MMM dd, yyyy")} />
              <Line type="monotone" dataKey="generated" stroke={COLORS.primary} strokeWidth={2} name="Generated" />
              <Line type="monotone" dataKey="interested" stroke={COLORS.gold} strokeWidth={2} name="Interested" />
              <Line type="monotone" dataKey="mutual" stroke={COLORS.green} strokeWidth={2} name="Mutual" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickLinks.map((link, idx) => (
            <button
              key={idx}
              onClick={() => navigate(link.link)}
              className="relative p-4 rounded-xl border-2 border-gray-100 hover:border-[#1C3A7A]/20 hover:shadow-md transition-all text-left group"
            >
              <div className={`w-10 h-10 ${link.color} rounded-xl flex items-center justify-center mb-2`}>
                <link.icon className="w-5 h-5 text-white" />
              </div>
              <p className="font-medium text-gray-900 text-sm">{link.title}</p>
              <p className="text-xs text-gray-500">{link.desc}</p>
              {link.badge > 0 && (
                <span className="absolute top-2 right-2 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                  {link.badge}
                </span>
              )}
              <ChevronRight className="absolute bottom-4 right-3 w-4 h-4 text-gray-300 group-hover:text-[#1C3A7A] transition-colors" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
};


// ==================== ONBOARDING TAB ====================
const OnboardingTab = ({ onboarding, onboardingDropoff }) => {
  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Completion Rate"
          value={`${onboardingDropoff?.summary.overall_completion_rate || 0}%`}
          icon={CheckCircle2}
          color="green"
        />
        <StatCard
          title="Avg. Time to Complete"
          value={`${onboardingDropoff?.insights.avg_completion_time_minutes || 0}m`}
          icon={Timer}
          color="blue"
        />
        <StatCard
          title="Biggest Drop-off"
          value={onboardingDropoff?.insights.biggest_drop_off_step || "N/A"}
          subtext={`${onboardingDropoff?.insights.biggest_drop_off_rate || 0}% drop`}
          icon={XCircle}
          color="red"
        />
        <StatCard
          title="In Progress"
          value={onboarding?.summary.in_progress || 0}
          icon={Hourglass}
          color="yellow"
        />
      </div>

      {/* Drop-off Funnel */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-red-500" />
          Onboarding Drop-off Analysis
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 border-b">
                <th className="pb-3">Step</th>
                <th className="pb-3 text-center">Completed</th>
                <th className="pb-3 text-center">Dropped Before</th>
                <th className="pb-3 text-center">Drop-off Rate</th>
                <th className="pb-3 text-center">Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {onboardingDropoff?.funnel.map((step, idx) => (
                <tr key={idx} className="border-b last:border-0">
                  <td className="py-3 font-medium">{step.name}</td>
                  <td className="py-3 text-center">{step.completed}</td>
                  <td className="py-3 text-center text-red-600">{step.dropped_before}</td>
                  <td className="py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      step.drop_off_rate > 20 ? "bg-red-100 text-red-700" :
                      step.drop_off_rate > 10 ? "bg-yellow-100 text-yellow-700" :
                      "bg-green-100 text-green-700"
                    }`}>
                      {step.drop_off_rate}%
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-[#1C3A7A] h-2 rounded-full" 
                        style={{ width: `${step.cumulative_completion}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">{step.cumulative_completion}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Original Funnel Visual */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-500" />
            Onboarding Progress Funnel
          </h2>
          <div className="text-sm">
            <span className="text-gray-500">Completion Rate: </span>
            <span className="font-bold text-green-600">{onboarding?.summary.completion_rate || 0}%</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {onboarding?.funnel.map((step, idx) => (
              <div key={idx} className="flex-1 min-w-[100px]">
                <div className="relative">
                  <div 
                    className="h-16 rounded-lg flex items-center justify-center transition-all"
                    style={{ 
                      backgroundColor: step.percentage > 50 ? COLORS.green : step.percentage > 20 ? COLORS.gold : COLORS.red,
                      opacity: 0.2 + (step.percentage / 100) * 0.8
                    }}
                  >
                    <span className="text-sm font-bold text-gray-800">{step.percentage}%</span>
                  </div>
                  {idx < (onboarding?.funnel.length - 1) && (
                    <div className="absolute top-1/2 -right-1 transform -translate-y-1/2">
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600 text-center mt-2 truncate" title={step.step_name}>
                  {step.step_name.length > 12 ? step.step_name.slice(0, 12) + "..." : step.step_name}
                </p>
                <p className="text-xs text-gray-400 text-center">{step.completed}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="p-3 bg-green-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-green-600">{onboarding?.summary.fully_onboarded || 0}</p>
            <p className="text-xs text-gray-600">Fully Onboarded</p>
          </div>
          <div className="p-3 bg-yellow-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-yellow-600">{onboarding?.summary.in_progress || 0}</p>
            <p className="text-xs text-gray-600">In Progress</p>
          </div>
          <div className="p-3 bg-gray-100 rounded-xl text-center">
            <p className="text-2xl font-bold text-gray-600">{onboarding?.summary.never_started || 0}</p>
            <p className="text-xs text-gray-600">Never Started</p>
          </div>
        </div>
      </div>
    </>
  );
};


// ==================== MESSAGING TAB ====================
const MessagingTab = ({ guidedMessaging }) => {
  const stageData = guidedMessaging ? [
    { name: "In Guided Phase", value: guidedMessaging.conversations.in_guided_phase, color: COLORS.blue },
    { name: "Free Chat Unlocked", value: guidedMessaging.conversations.unlocked_free_chat, color: COLORS.green },
  ] : [];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Conversations"
          value={guidedMessaging?.conversations.total || 0}
          icon={MessageSquare}
          color="blue"
        />
        <StatCard
          title="Unlock Rate"
          value={`${guidedMessaging?.unlock_metrics.unlock_rate || 0}%`}
          subtext="Guided → Free chat"
          icon={Unlock}
          color="green"
        />
        <StatCard
          title="Avg. Prompts to Unlock"
          value={guidedMessaging?.unlock_metrics.avg_prompts_before_unlock || 0}
          subtext="Per user"
          icon={MessageCircle}
          color="purple"
        />
        <StatCard
          title="Response Rate"
          value={`${guidedMessaging?.messages.response_rate || 0}%`}
          subtext="Both users engaged"
          icon={Send}
          color="teal"
        />
      </div>

      {/* Conversation Stage Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            Conversation Stages
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {stageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {stageData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Message Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-500" />
            Message Statistics
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
              <span className="text-gray-700">Guided Messages</span>
              <span className="text-xl font-bold text-blue-600">{guidedMessaging?.messages.total_guided || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
              <span className="text-gray-700">Free-form Messages</span>
              <span className="text-xl font-bold text-green-600">{guidedMessaging?.messages.total_freeform || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-xl">
              <span className="text-gray-700">Stuck Conversations</span>
              <span className="text-xl font-bold text-yellow-600">{guidedMessaging?.conversations.stuck_conversations || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
              <span className="text-gray-700">Avg. Time to Unlock</span>
              <span className="text-xl font-bold text-purple-600">{guidedMessaging?.unlock_metrics.avg_time_to_unlock_hours || 0}h</span>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Breakdown */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ThumbsUp className="w-5 h-5 text-[#FAB710]" />
          Engagement Breakdown
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="text-center p-6 bg-green-50 rounded-xl">
            <p className="text-4xl font-bold text-green-600">{guidedMessaging?.engagement.both_users_engaged || 0}</p>
            <p className="text-gray-600 mt-2">Mutual Engagement</p>
            <p className="text-sm text-gray-500">Both users responded</p>
          </div>
          <div className="text-center p-6 bg-yellow-50 rounded-xl">
            <p className="text-4xl font-bold text-yellow-600">{guidedMessaging?.engagement.one_sided || 0}</p>
            <p className="text-gray-600 mt-2">One-sided Conversations</p>
            <p className="text-sm text-gray-500">Only one user engaged</p>
          </div>
        </div>
      </div>
    </>
  );
};


// ==================== ENGAGEMENT TAB ====================
const EngagementTab = ({ dailyEngagement }) => {
  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="View Rate (7d)"
          value={`${dailyEngagement?.last_7_days.view_rate || 0}%`}
          subtext={`${dailyEngagement?.last_7_days.matches_viewed || 0} viewed`}
          icon={Eye}
          color="blue"
        />
        <StatCard
          title="Interest Rate (7d)"
          value={`${dailyEngagement?.last_7_days.interest_rate || 0}%`}
          icon={Heart}
          color="pink"
        />
        <StatCard
          title="Active Today"
          value={dailyEngagement?.today.active_users || 0}
          subtext={`${dailyEngagement?.today.daily_active_rate || 0}% DAU`}
          icon={Users}
          color="green"
        />
        <StatCard
          title="Avg Batch Size"
          value={dailyEngagement?.batch_performance.avg_daily_batch_size || 0}
          subtext="matches/day"
          icon={Target}
          color="purple"
        />
      </div>

      {/* Detailed Engagement */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Last 7 Days Performance
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-700">Matches Served</span>
              <span className="text-xl font-bold text-gray-800">{dailyEngagement?.last_7_days.total_matches_served || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
              <span className="text-gray-700">Matches Viewed</span>
              <span className="text-xl font-bold text-blue-600">{dailyEngagement?.last_7_days.matches_viewed || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
              <span className="text-gray-700">View Rate</span>
              <span className="text-xl font-bold text-green-600">{dailyEngagement?.last_7_days.view_rate || 0}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-pink-50 rounded-xl">
              <span className="text-gray-700">Interest Rate</span>
              <span className="text-xl font-bold text-pink-600">{dailyEngagement?.last_7_days.interest_rate || 0}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Today&apos;s Activity
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
              <span className="text-gray-700">Active Users</span>
              <span className="text-xl font-bold text-green-600">{dailyEngagement?.today.active_users || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
              <span className="text-gray-700">Users with Matches</span>
              <span className="text-xl font-bold text-blue-600">{dailyEngagement?.today.users_with_matches || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
              <span className="text-gray-700">Daily Active Rate</span>
              <span className="text-xl font-bold text-purple-600">{dailyEngagement?.today.daily_active_rate || 0}%</span>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-medium text-gray-700 mb-2">Batch Performance</h3>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Avg engaged per batch:</span>
              <span className="font-bold">{dailyEngagement?.batch_performance.avg_engaged_per_batch || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// ==================== SUBSCRIPTIONS TAB ====================
const SubscriptionsTab = ({ subscriptionFunnel }) => {
  const distributionData = subscriptionFunnel ? [
    { name: "Free", value: subscriptionFunnel.current_distribution.free, color: "#9CA3AF" },
    { name: "Premium", value: subscriptionFunnel.current_distribution.premium, color: COLORS.gold },
    { name: "Elite", value: subscriptionFunnel.current_distribution.elite, color: COLORS.primary }
  ] : [];

  return (
    <>
      {/* Live Stripe banner */}
      <div data-testid="stripe-revenue-banner" className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#1C3A7A] text-white rounded-xl px-4 py-3 text-sm">
        <span className="flex items-center gap-2 font-semibold">
          <DollarSign className="w-4 h-4 text-[#FAB710]" />
          Revenue pulled live from Stripe
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${subscriptionFunnel?.revenue?.stripe_mode === 'live' ? 'bg-green-500 text-white' : 'bg-[#FAB710] text-[#1C3A7A]'}`}>
          {(subscriptionFunnel?.revenue?.stripe_mode || 'test').toUpperCase()} MODE
        </span>
        <span className="text-white/80">Total collected: <b className="text-white">${subscriptionFunnel?.revenue?.total_collected ?? 0}</b></span>
        <span className="text-white/80">Comped accounts excluded: <b className="text-white">{subscriptionFunnel?.revenue?.comped_accounts ?? 0}</b></span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Free → Paid"
          value={`${subscriptionFunnel?.conversion_rates.free_to_paid || 0}%`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Paying Subscribers"
          value={subscriptionFunnel?.revenue?.paying_subscribers ?? 0}
          subtext={`${subscriptionFunnel?.revenue?.stripe_subscribers ?? 0} web · ${subscriptionFunnel?.revenue?.mobile_subscribers ?? 0} mobile`}
          icon={Crown}
          color="gold"
        />
        <StatCard
          title="Churn Rate"
          value={`${subscriptionFunnel?.retention.churn_rate || 0}%`}
          icon={XCircle}
          color="red"
        />
        <StatCard
          title="MRR (Stripe)"
          value={`$${subscriptionFunnel?.revenue?.mrr ?? 0}`}
          subtext={`${subscriptionFunnel?.revenue?.active_stripe_subscriptions ?? 0} active subscriptions`}
          icon={DollarSign}
          color="green"
        />
      </div>

      {/* Distribution & Funnel */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#FAB710]" />
            Subscription Distribution
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {distributionData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Conversion Funnel
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Free Users</span>
                <span className="font-bold">{subscriptionFunnel?.current_distribution.free || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-gray-400 h-3 rounded-full" style={{ width: `${subscriptionFunnel?.current_distribution.free_pct || 0}%` }}></div>
              </div>
            </div>
            <div className="p-4 bg-[#FAB710]/10 rounded-xl border-2 border-[#FAB710]/30">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Premium (Recovery+)</span>
                <span className="font-bold text-[#FAB710]">{subscriptionFunnel?.current_distribution.premium || 0}</span>
              </div>
              <div className="w-full bg-[#FAB710]/20 rounded-full h-3">
                <div className="bg-[#FAB710] h-3 rounded-full" style={{ width: `${subscriptionFunnel?.current_distribution.premium_pct || 0}%` }}></div>
              </div>
            </div>
            <div className="p-4 bg-[#1C3A7A]/10 rounded-xl border-2 border-[#1C3A7A]/30">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Recovery Elite</span>
                <span className="font-bold text-[#1C3A7A]">{subscriptionFunnel?.current_distribution.elite || 0}</span>
              </div>
              <div className="w-full bg-[#1C3A7A]/20 rounded-full h-3">
                <div className="bg-[#1C3A7A] h-3 rounded-full" style={{ width: `${subscriptionFunnel?.current_distribution.elite_pct || 0}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Last 30 Days & Retention */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Last 30 Days
          </h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{subscriptionFunnel?.last_30_days.new_signups || 0}</p>
              <p className="text-xs text-gray-600">New Signups</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl">
              <p className="text-2xl font-bold text-green-600">{subscriptionFunnel?.last_30_days.converted_to_paid || 0}</p>
              <p className="text-xs text-gray-600">Converted</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl">
              <p className="text-2xl font-bold text-purple-600">{subscriptionFunnel?.last_30_days.conversion_rate || 0}%</p>
              <p className="text-xs text-gray-600">Conv. Rate</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-500" />
            Retention
          </h2>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="p-4 bg-red-50 rounded-xl">
              <p className="text-2xl font-bold text-red-600">{subscriptionFunnel?.retention.churned_users || 0}</p>
              <p className="text-xs text-gray-600">Churned Users</p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl">
              <p className="text-2xl font-bold text-red-600">{subscriptionFunnel?.retention.churn_rate || 0}%</p>
              <p className="text-xs text-gray-600">Churn Rate</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// ==================== RECOVERY INSIGHTS TAB ====================
const RecoveryTab = ({ recoveryInsights }) => {
  if (!recoveryInsights) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
        <p className="text-gray-500">Loading recovery insights...</p>
      </div>
    );
  }

  const terminologyData = [
    { name: "Sober", value: recoveryInsights.terminology_preference?.sober || 0, color: COLORS.blue },
    { name: "Clean", value: recoveryInsights.terminology_preference?.clean || 0, color: COLORS.green },
    { name: "Not Set", value: recoveryInsights.terminology_preference?.not_set || 0, color: "#9CA3AF" }
  ];

  const programData = [
    { name: "AA", value: recoveryInsights.program_distribution?.AA || 0 },
    { name: "NA", value: recoveryInsights.program_distribution?.NA || 0 },
    { name: "SMART", value: recoveryInsights.program_distribution?.SMART_Recovery || 0 },
    { name: "Refuge", value: recoveryInsights.program_distribution?.Refuge_Recovery || 0 },
    { name: "Other", value: recoveryInsights.program_distribution?.Other || 0 }
  ];

  const milestoneData = [
    { name: "90d-1y", value: recoveryInsights.cleantime_milestones?.["90_days_to_1_year"] || 0, color: COLORS.green },
    { name: "1-2y", value: recoveryInsights.cleantime_milestones?.["1_to_2_years"] || 0, color: COLORS.blue },
    { name: "2-5y", value: recoveryInsights.cleantime_milestones?.["2_to_5_years"] || 0, color: COLORS.purple },
    { name: "5y+", value: recoveryInsights.cleantime_milestones?.["5_plus_years"] || 0, color: COLORS.gold }
  ];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Avg Cleantime"
          value={`${recoveryInsights?.averages.avg_cleantime_years || 0}y`}
          subtext={`${recoveryInsights?.averages.avg_cleantime_days || 0} days`}
          icon={Leaf}
          color="green"
        />
        <StatCard
          title="'Sober' Preference"
          value={`${recoveryInsights?.terminology_preference.sober_pct || 0}%`}
          icon={Award}
          color="blue"
        />
        <StatCard
          title="'Clean' Preference"
          value={`${recoveryInsights?.terminology_preference.clean_pct || 0}%`}
          icon={Award}
          color="green"
        />
        <StatCard
          title="Non-Negotiable"
          value={recoveryInsights?.recovery_importance.non_negotiable || 0}
          subtext="users"
          icon={Shield}
          color="purple"
        />
      </div>

      {/* Terminology & Programs */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-500" />
            Terminology Preference
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={terminologyData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {terminologyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {terminologyData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-green-500" />
            Program Distribution
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={programData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="value" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Cleantime Milestones & Importance */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#FAB710]" />
            Cleantime Milestones
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {milestoneData.map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl text-center" style={{ backgroundColor: `${item.color}15` }}>
                <p className="text-2xl font-bold" style={{ color: item.color }}>{item.value}</p>
                <p className="text-sm text-gray-600">{item.name}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Recovery Importance
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
              <span className="font-medium text-gray-700">Non-Negotiable</span>
              <span className="text-lg font-bold text-purple-600">{recoveryInsights?.recovery_importance.non_negotiable || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
              <span className="font-medium text-gray-700">Very Important</span>
              <span className="text-lg font-bold text-blue-600">{recoveryInsights?.recovery_importance.very_important || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
              <span className="font-medium text-gray-700">Important</span>
              <span className="text-lg font-bold text-green-600">{recoveryInsights?.recovery_importance.important || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-xl">
              <span className="font-medium text-gray-700">Flexible</span>
              <span className="text-lg font-bold text-gray-600">{recoveryInsights?.recovery_importance.flexible || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// ==================== SUCCESS METRICS TAB ====================
const SuccessTab = ({ successMetrics }) => {
  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="7+ Day Convos"
          value={successMetrics?.conversation_longevity.lasting_7_plus_days || 0}
          subtext={`${successMetrics?.conversation_longevity.longevity_rate || 0}% rate`}
          icon={Calendar}
          color="green"
        />
        <StatCard
          title="Deep Convos (10+)"
          value={successMetrics?.conversation_depth.with_10_plus_messages || 0}
          icon={MessageSquare}
          color="blue"
        />
        <StatCard
          title="Avg Messages"
          value={successMetrics?.conversation_depth.avg_messages_per_conversation || 0}
          subtext="per conversation"
          icon={MessageCircle}
          color="purple"
        />
        <StatCard
          title="Popular Users"
          value={successMetrics?.engagement.popular_users_3_plus_matches || 0}
          subtext="3+ mutual matches"
          icon={Heart}
          color="pink"
        />
      </div>

      {/* Conversation Quality */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Timer className="w-5 h-5 text-green-500" />
            Conversation Longevity
          </h2>
          <div className="text-center p-6 bg-green-50 rounded-xl mb-4">
            <p className="text-5xl font-bold text-green-600">{successMetrics?.conversation_longevity.lasting_7_plus_days || 0}</p>
            <p className="text-gray-600 mt-2">Conversations lasting 7+ days</p>
            <p className="text-sm text-gray-500">{successMetrics?.conversation_longevity.longevity_rate || 0}% of all conversations</p>
          </div>
          <p className="text-sm text-gray-500 text-center">
            Conversations that remain active after a week indicate strong compatibility and genuine interest.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            Conversation Depth
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-700">10+ Messages</p>
                <p className="text-sm text-gray-500">Getting to know each other</p>
              </div>
              <span className="text-2xl font-bold text-blue-600">{successMetrics?.conversation_depth.with_10_plus_messages || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-purple-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-700">20+ Messages</p>
                <p className="text-sm text-gray-500">Highly engaged</p>
              </div>
              <span className="text-2xl font-bold text-purple-600">{successMetrics?.conversation_depth.with_20_plus_messages || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-100 rounded-xl">
              <div>
                <p className="font-medium text-gray-700">Average Messages</p>
                <p className="text-sm text-gray-500">Per conversation</p>
              </div>
              <span className="text-2xl font-bold text-gray-700">{successMetrics?.conversation_depth.avg_messages_per_conversation || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement & Success */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Video className="w-5 h-5 text-teal-500" />
            Video Calls
          </h2>
          <div className="text-center p-6 bg-teal-50 rounded-xl">
            <p className="text-5xl font-bold text-teal-600">{successMetrics?.engagement.video_calls_initiated || 0}</p>
            <p className="text-gray-600 mt-2">Video Calls Initiated</p>
            <p className="text-sm text-gray-500 mt-2">Users taking the next step to meet face-to-face</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#FAB710]" />
            Success Stories
          </h2>
          <div className="text-center p-6 bg-[#FAB710]/10 rounded-xl">
            <p className="text-5xl font-bold text-[#FAB710]">{successMetrics?.success_stories.reported || 0}</p>
            <p className="text-gray-600 mt-2">Reported Success Stories</p>
            <p className="text-sm text-gray-500 mt-2">Users who found meaningful connections</p>
          </div>
        </div>
      </div>
    </>
  );
};


// ==================== REFERRAL PROGRAM TAB ====================
const ReferralTab = ({ referralProgram }) => {
  const trendData = referralProgram?.trends || [];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Referrals"
          value={referralProgram?.overview.total_referrals || 0}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="This Month"
          value={referralProgram?.overview.monthly_referrals || 0}
          subtext={`${referralProgram?.overview.mom_growth > 0 ? '+' : ''}${referralProgram?.overview.mom_growth || 0}% MoM`}
          icon={TrendingUp}
          color={referralProgram?.overview.mom_growth >= 0 ? "green" : "red"}
        />
        <StatCard
          title="Unique Referrers"
          value={referralProgram?.overview.unique_referrers || 0}
          icon={Award}
          color="purple"
        />
        <StatCard
          title="Premium Days Given"
          value={referralProgram?.rewards.total_premium_days_awarded || 0}
          subtext={`$${referralProgram?.rewards.estimated_value || 0} value`}
          icon={Crown}
          color="gold"
        />
      </div>

      {/* Badge Distribution & Effectiveness */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#FAB710]" />
            Badge Distribution
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🤝</span>
                <div>
                  <p className="font-medium text-gray-700">Connector</p>
                  <p className="text-xs text-gray-500">3+ referrals</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-blue-600">{referralProgram?.badges.connector || 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🌱</span>
                <div>
                  <p className="font-medium text-gray-700">Community Builder</p>
                  <p className="text-xs text-gray-500">5+ referrals</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-green-600">{referralProgram?.badges.community_builder || 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-[#FAB710]/10 rounded-xl border-2 border-[#FAB710]/30">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🏆</span>
                <div>
                  <p className="font-medium text-gray-700">Recovery Champion</p>
                  <p className="text-xs text-gray-500">10+ referrals</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-[#FAB710]">{referralProgram?.badges.recovery_champion || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-green-500" />
            Referral Effectiveness
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Referred Users Active</span>
                <span className="font-bold text-blue-600">{referralProgram?.effectiveness.referred_users_active || 0}</span>
              </div>
              <p className="text-xs text-gray-500">Completed their profile</p>
            </div>
            <div className="p-4 bg-pink-50 rounded-xl">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Found Matches</span>
                <span className="font-bold text-pink-600">{referralProgram?.effectiveness.referred_users_with_matches || 0}</span>
              </div>
              <p className="text-xs text-gray-500">Made at least one mutual match</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Match Rate</span>
                <span className="font-bold text-green-600">{referralProgram?.effectiveness.match_rate || 0}%</span>
              </div>
              <p className="text-xs text-gray-500">% of referred users who matched</p>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Trend */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#1C3A7A]" />
          Referral Trend (14 days)
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(d) => {
                  const date = new Date(d);
                  return `${date.getMonth()+1}/${date.getDate()}`;
                }}
                fontSize={12}
              />
              <YAxis fontSize={12} />
              <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
              <Area 
                type="monotone" 
                dataKey="referrals" 
                stroke={COLORS.primary} 
                fill={COLORS.primary}
                fillOpacity={0.2}
                strokeWidth={2}
                name="Referrals"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Referrers */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#FAB710]" />
          Top Referrers
        </h2>
        {referralProgram?.top_referrers?.length > 0 ? (
          <div className="space-y-3">
            {referralProgram.top_referrers.map((person, idx) => (
              <div 
                key={idx}
                className={`flex items-center justify-between p-3 rounded-xl ${
                  idx === 0 ? "bg-[#FAB710]/10 border-2 border-[#FAB710]/30" :
                  idx === 1 ? "bg-gray-100" :
                  idx === 2 ? "bg-orange-50" :
                  "bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? "bg-[#FAB710] text-white" :
                    idx === 1 ? "bg-gray-400 text-white" :
                    idx === 2 ? "bg-orange-400 text-white" :
                    "bg-gray-300 text-gray-700"
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-800">{person.name}</p>
                    {person.badges?.length > 0 && (
                      <div className="flex gap-1">
                        {person.badges.includes("recovery_champion") && <span>🏆</span>}
                        {person.badges.includes("community_builder") && <span>🌱</span>}
                        {person.badges.includes("connector") && <span>🤝</span>}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-xl font-bold text-[#1C3A7A]">{person.referrals}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No referrals yet</p>
          </div>
        )}
      </div>
    </>
  );
};


// ==================== REUSABLE COMPONENTS ====================

// ==================== SPONSOR & SUPPORT TAB ====================
const SponsorSupportTab = ({ sponsorSupport }) => {
  const sponsorStatusData = sponsorSupport ? [
    { name: "Has Sponsor", value: sponsorSupport.sponsor_status.has_sponsor, color: COLORS.green },
    { name: "Looking", value: sponsorSupport.sponsor_status.looking, color: COLORS.gold },
    { name: "No Sponsor", value: sponsorSupport.sponsor_status.no_sponsor, color: COLORS.red },
    { name: "Private", value: sponsorSupport.sponsor_status.private, color: "#9CA3AF" }
  ] : [];

  const homeGroupData = sponsorSupport ? [
    { name: "Has Group", value: sponsorSupport.home_group.has_home_group, color: COLORS.blue },
    { name: "Multiple", value: sponsorSupport.home_group.multiple, color: COLORS.green },
    { name: "Looking", value: sponsorSupport.home_group.looking, color: COLORS.gold },
    { name: "None", value: sponsorSupport.home_group.no_home_group, color: "#9CA3AF" }
  ] : [];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Have Sponsors"
          value={`${sponsorSupport?.sponsor_status.has_sponsor_pct || 0}%`}
          subtext={`${sponsorSupport?.sponsor_status.has_sponsor || 0} users`}
          icon={UserCheck}
          color="green"
        />
        <StatCard
          title="Sponsor Others"
          value={`${sponsorSupport?.sponsoring_others.sponsor_pct || 0}%`}
          subtext={`${sponsorSupport?.sponsoring_others.yes || 0} active sponsors`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Home Groups"
          value={`${sponsorSupport?.home_group.home_group_pct || 0}%`}
          subtext="Have home groups"
          icon={Home}
          color="purple"
        />
        <StatCard
          title="Match Advantage"
          value={`+${sponsorSupport?.match_correlation.sponsor_advantage || 0}%`}
          subtext="Sponsor users vs non"
          icon={TrendingUp}
          color={sponsorSupport?.match_correlation.sponsor_advantage > 0 ? "green" : "red"}
        />
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-500" />
            Sponsor Status Distribution
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sponsorStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {sponsorStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {sponsorStatusData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-blue-500" />
            Home Group Distribution
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={homeGroupData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {homeGroupData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {homeGroupData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partner Importance & Correlation */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Partner Sponsor Importance
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
              <span className="text-gray-700">Very Important</span>
              <span className="font-bold text-green-600">{sponsorSupport?.partner_importance.very_important || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
              <span className="text-gray-700">Somewhat Important</span>
              <span className="font-bold text-blue-600">{sponsorSupport?.partner_importance.somewhat_important || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-xl">
              <span className="text-gray-700">Not Important</span>
              <span className="font-bold text-gray-600">{sponsorSupport?.partner_importance.not_important || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#1C3A7A]" />
            Sponsor vs Match Success
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-xl">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-700">Users WITH Sponsors</p>
                  <p className="text-sm text-gray-500">Match success rate</p>
                </div>
                <span className="text-3xl font-bold text-green-600">{sponsorSupport?.match_correlation.with_sponsor_match_rate || 0}%</span>
              </div>
            </div>
            <div className="p-4 bg-gray-100 rounded-xl">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-700">Users WITHOUT Sponsors</p>
                  <p className="text-sm text-gray-500">Match success rate</p>
                </div>
                <span className="text-3xl font-bold text-gray-600">{sponsorSupport?.match_correlation.without_sponsor_match_rate || 0}%</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            {sponsorSupport?.insights.sponsor_engagement === "high" ? "💚 High sponsor engagement in the community" :
             sponsorSupport?.insights.sponsor_engagement === "moderate" ? "💛 Moderate sponsor engagement" :
             "⚠️ Low sponsor engagement - consider promoting"}
          </p>
        </div>
      </div>
    </>
  );
};


// ==================== FILTERS USAGE TAB ====================
const FiltersUsageTab = ({ filtersUsage }) => {
  const categoryData = filtersUsage ? [
    { name: "Recovery", value: filtersUsage.categories.recovery, color: COLORS.green },
    { name: "Support", value: filtersUsage.categories.support, color: COLORS.blue },
    { name: "Location", value: filtersUsage.categories.location, color: COLORS.purple },
    { name: "Lifestyle", value: filtersUsage.categories.lifestyle, color: COLORS.gold },
    { name: "Substances", value: filtersUsage.categories.substances, color: COLORS.red }
  ] : [];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Elite Users"
          value={filtersUsage?.overview.elite_users || 0}
          icon={Crown}
          color="gold"
        />
        <StatCard
          title="Using Filters"
          value={filtersUsage?.overview.users_with_filters || 0}
          subtext={`${filtersUsage?.overview.adoption_rate || 0}% adoption`}
          icon={Filter}
          color="blue"
        />
        <StatCard
          title="Filter Match Rate"
          value={`${filtersUsage?.effectiveness.filter_users_match_rate || 0}`}
          subtext="Avg matches/user"
          icon={Heart}
          color="pink"
        />
        <StatCard
          title="Filters Work"
          value={filtersUsage?.effectiveness.filters_improve_matches ? "Yes ✓" : "No ✗"}
          subtext="Improve match rates"
          icon={CheckCircle2}
          color={filtersUsage?.effectiveness.filters_improve_matches ? "green" : "red"}
        />
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#1C3A7A]" />
            Most Popular Filters
          </h2>
          <div className="space-y-3">
            {filtersUsage?.popular_filters.slice(0, 8).map((filter, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500 w-6">{idx + 1}</span>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-700">{filter.filter}</span>
                    <span className="text-sm font-medium text-gray-900">{filter.count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-[#1C3A7A] h-2 rounded-full" 
                      style={{ width: `${(filter.count / (filtersUsage?.overview.users_with_filters || 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#FAB710]" />
            Filter Categories
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {categoryData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Filter Usage */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Filter Usage</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {filtersUsage && Object.entries(filtersUsage.filter_usage).map(([key, value]) => (
            <div key={key} className="p-3 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">{key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
              <p className="text-xl font-bold text-gray-800">{value}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};


// ==================== SAFETY & MODERATION TAB ====================
const SafetyModerationTab = ({ safetyModeration, navigate }) => {
  const reasonsData = safetyModeration ? Object.entries(safetyModeration.report_reasons).map(([key, value]) => ({
    name: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
    value
  })) : [];

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Pending Reports"
          value={safetyModeration?.reports.pending || 0}
          subtext={`${safetyModeration?.reports.total || 0} total`}
          icon={Flag}
          color="red"
        />
        <StatCard
          title="Resolution Rate"
          value={`${safetyModeration?.reports.resolution_rate || 0}%`}
          icon={CheckCircle2}
          color="green"
        />
        <StatCard
          title="Moderation Queue"
          value={safetyModeration?.moderation_queue.total_queue || 0}
          subtext="Items pending"
          icon={AlertTriangle}
          color="yellow"
        />
        <StatCard
          title="Avg Response"
          value={`${safetyModeration?.response_metrics.avg_response_hours || 0}h`}
          subtext={`Target: ${safetyModeration?.response_metrics.target_hours || 24}h`}
          icon={Clock}
          color={safetyModeration?.response_metrics.avg_response_hours <= 24 ? "green" : "red"}
        />
      </div>

      {/* Quick Actions & Queue */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Moderation Queue
          </h2>
          <div className="space-y-3">
            <div 
              onClick={() => navigate("/admin/reports")}
              className="flex items-center justify-between p-4 bg-red-50 rounded-xl cursor-pointer hover:bg-red-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Flag className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium text-gray-800">Pending Reports</p>
                  <p className="text-sm text-gray-500">User reports to review</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-red-600">{safetyModeration?.reports.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium text-gray-800">Flagged Photos</p>
                  <p className="text-sm text-gray-500">Photos pending review</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-orange-600">{safetyModeration?.moderation_queue.flagged_photos || 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium text-gray-800">Urgent Tickets</p>
                  <p className="text-sm text-gray-500">High priority support</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-purple-600">{safetyModeration?.support.urgent_tickets || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            User Actions
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-orange-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-orange-600">{safetyModeration?.user_actions.suspended || 0}</p>
              <p className="text-sm text-gray-600">Suspended Users</p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-red-600">{safetyModeration?.user_actions.banned || 0}</p>
              <p className="text-sm text-gray-600">Banned Users</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-blue-600">{safetyModeration?.blocks.total || 0}</p>
              <p className="text-sm text-gray-600">Total Blocks</p>
            </div>
            <div className="p-4 bg-gray-100 rounded-xl text-center">
              <p className="text-3xl font-bold text-gray-600">{safetyModeration?.blocks.recent_7_days || 0}</p>
              <p className="text-sm text-gray-600">Blocks (7 days)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Reasons & Trends */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Flag className="w-5 h-5 text-red-500" />
            Report Reasons
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reasonsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" fontSize={12} />
                <YAxis dataKey="name" type="category" fontSize={10} width={100} />
                <Tooltip />
                <Bar dataKey="value" fill={COLORS.red} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#1C3A7A]" />
            Report Trend (14 days)
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={safetyModeration?.trends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" fontSize={10} tickFormatter={(val) => val.slice(5)} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Area type="monotone" dataKey="reports" stroke={COLORS.red} fill={COLORS.red} fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Reported Users */}
      {safetyModeration?.top_reported_users?.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Most Reported Users
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-3">User</th>
                  <th className="pb-3">Email</th>
                  <th className="pb-3 text-right">Reports</th>
                </tr>
              </thead>
              <tbody>
                {safetyModeration.top_reported_users.map((user, idx) => (
                  <tr key={idx} className="border-b last:border-0">
                    <td className="py-3 font-medium text-gray-800">{user.name}</td>
                    <td className="py-3 text-gray-600">{user.email}</td>
                    <td className="py-3 text-right">
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                        {user.report_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};


// ==================== ACTIVITY FEED TAB ====================
const ActivityFeedTab = ({ activityFeed }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case "signup": return <UserPlus className="w-4 h-4" />;
      case "match": return <Heart className="w-4 h-4" />;
      case "conversation": return <MessageCircle className="w-4 h-4" />;
      case "subscription": return <Crown className="w-4 h-4" />;
      case "referral": return <Users className="w-4 h-4" />;
      case "report": return <Flag className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getActivityColor = (color) => {
    switch (color) {
      case "green": return "bg-green-100 text-green-600";
      case "pink": return "bg-pink-100 text-pink-600";
      case "blue": return "bg-blue-100 text-blue-600";
      case "gold": return "bg-[#FAB710]/20 text-[#FAB710]";
      case "purple": return "bg-purple-100 text-purple-600";
      case "red": return "bg-red-100 text-red-600";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return date.toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Active Now"
          value={activityFeed?.summary.active_now || 0}
          subtext="Users online"
          icon={Radio}
          color="green"
        />
        <StatCard
          title="Signups (24h)"
          value={activityFeed?.summary.signups_24h || 0}
          icon={UserPlus}
          color="blue"
        />
        <StatCard
          title="Matches (24h)"
          value={activityFeed?.summary.matches_24h || 0}
          icon={Heart}
          color="pink"
        />
        <StatCard
          title="Messages (24h)"
          value={activityFeed?.summary.messages_24h || 0}
          icon={MessageCircle}
          color="purple"
        />
      </div>

      {/* Activity Feed */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Radio className="w-5 h-5 text-green-500 animate-pulse" />
            Live Activity Feed
          </h2>
          <span className="text-sm text-gray-500">
            Last updated: {activityFeed?.last_updated ? formatTime(activityFeed.last_updated) : "N/A"}
          </span>
        </div>
        
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {activityFeed?.activities?.length > 0 ? (
            activityFeed.activities.map((activity, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.color)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{activity.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatTime(activity.timestamp)}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getActivityColor(activity.color)}`}>
                  {activity.type}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No recent activity</p>
              <p className="text-sm text-gray-400 mt-1">Activity will appear here as it happens</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};


const MetricCard = ({ title, value, subtext, icon: Icon, trend, highlight }) => (
  <div className={`rounded-xl p-4 ${highlight ? "bg-[#FAB710]" : "bg-white/10"}`}>
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-5 h-5 ${highlight ? "text-white" : "text-white/70"}`} />
      {trend && (
        <span className={`flex items-center gap-1 text-xs ${trend === "up" ? "text-green-300" : "text-red-300"}`}>
          {trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        </span>
      )}
    </div>
    <p className={`text-2xl font-bold ${highlight ? "text-white" : "text-white"}`}>{value}</p>
    <p className={`text-sm ${highlight ? "text-white/80" : "text-white/60"}`}>{title}</p>
    {subtext && <p className={`text-xs mt-1 ${highlight ? "text-white/70" : "text-white/50"}`}>{subtext}</p>}
  </div>
);

const StatCard = ({ title, value, subtext, icon: Icon, color }) => {
  const colorClasses = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
    yellow: "bg-yellow-50 text-yellow-600",
    purple: "bg-purple-50 text-purple-600",
    pink: "bg-pink-50 text-pink-600",
    teal: "bg-teal-50 text-teal-600",
    gold: "bg-[#FAB710]/10 text-[#FAB710]"
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]?.split(" ")[0]}`}>
          <Icon className={`w-5 h-5 ${colorClasses[color]?.split(" ")[1]}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
          {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
        </div>
      </div>
    </div>
  );
};

// ==================== AARRR METRICS TAB ====================
const AARRRTab = ({ aarrrMetrics }) => {
  if (!aarrrMetrics) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
    );
  }

  const { acquisition, activation, retention, referral, revenue, summary } = aarrrMetrics;

  // Health score color
  const getHealthColor = (score) => {
    if (score >= 70) return "text-green-600 bg-green-50";
    if (score >= 50) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  return (
    <>
      {/* Business Health Score */}
      <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-1">Business Health Score</h2>
            <p className="text-white/70 text-sm">Based on AARRR metrics performance</p>
          </div>
          <div className={`text-5xl font-bold px-6 py-3 rounded-xl ${getHealthColor(summary?.health_score || 0)}`}>
            {summary?.health_score || 0}
          </div>
        </div>
      </div>

      {/* AARRR Cards Row */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-800">Acquisition</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">{acquisition?.signups_month || 0}</p>
          <p className="text-xs text-blue-600/70">This month</p>
          <p className={`text-xs mt-1 ${acquisition?.mom_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {acquisition?.mom_growth >= 0 ? '↑' : '↓'} {Math.abs(acquisition?.mom_growth || 0)}% MoM
          </p>
        </div>

        <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">Activation</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{activation?.activation_rate || 0}%</p>
          <p className="text-xs text-green-600/70">Completed onboarding</p>
          <p className="text-xs mt-1 text-gray-500">{activation?.total_activated || 0} users</p>
        </div>

        <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-purple-800">Retention</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">{retention?.stickiness || 0}%</p>
          <p className="text-xs text-purple-600/70">DAU/MAU Stickiness</p>
          <p className="text-xs mt-1 text-gray-500">{retention?.churn_rate || 0}% churn</p>
        </div>

        <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-orange-600" />
            <span className="font-semibold text-orange-800">Referral</span>
          </div>
          <p className="text-3xl font-bold text-orange-600">{referral?.viral_coefficient || 0}</p>
          <p className="text-xs text-orange-600/70">K-Factor</p>
          <p className="text-xs mt-1 text-gray-500">{referral?.referral_rate || 0}% refer</p>
        </div>

        <div className="bg-emerald-50 rounded-xl p-4 border-2 border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-emerald-800">Revenue</span>
          </div>
          <p className="text-3xl font-bold text-emerald-600">${revenue?.mrr || 0}</p>
          <p className="text-xs text-emerald-600/70">MRR</p>
          <p className="text-xs mt-1 text-gray-500">{revenue?.conversion_rate || 0}% convert</p>
        </div>
      </div>

      {/* Acquisition Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-blue-500" />
          Acquisition - Where Users Come From
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Traffic Sources */}
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Traffic Sources</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                <span className="text-gray-700">Organic Signups</span>
                <div className="text-right">
                  <span className="text-xl font-bold text-blue-600">{acquisition?.sources?.organic || 0}</span>
                  <span className="text-sm text-gray-500 ml-2">({acquisition?.sources?.organic_pct || 0}%)</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                <span className="text-gray-700">Referred Signups</span>
                <div className="text-right">
                  <span className="text-xl font-bold text-orange-600">{acquisition?.sources?.referred || 0}</span>
                  <span className="text-sm text-gray-500 ml-2">({acquisition?.sources?.referred_pct || 0}%)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Signup Stats */}
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Signup Velocity</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-bold text-gray-800">{acquisition?.signups_today || 0}</p>
                <p className="text-xs text-gray-500">Today</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-bold text-gray-800">{acquisition?.signups_week || 0}</p>
                <p className="text-xs text-gray-500">This Week</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-bold text-gray-800">{acquisition?.signups_month || 0}</p>
                <p className="text-xs text-gray-500">This Month</p>
              </div>
            </div>
          </div>
        </div>

        {/* Acquisition Trend Chart */}
        <div className="mt-6">
          <h3 className="font-medium text-gray-700 mb-3">14-Day Signup Trend</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={acquisition?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Area type="monotone" dataKey="signups" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={2} name="Signups" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Activation Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-green-500" />
          Activation - First Value Moments
        </h2>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="p-4 bg-green-50 rounded-xl text-center">
            <p className="text-3xl font-bold text-green-600">{activation?.activation_rate || 0}%</p>
            <p className="text-sm text-gray-600">Profile Completion Rate</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-xl text-center">
            <p className="text-3xl font-bold text-blue-600">{activation?.interest_rate || 0}%</p>
            <p className="text-sm text-gray-600">Expressed Interest</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-xl text-center">
            <p className="text-3xl font-bold text-purple-600">{activation?.message_rate || 0}%</p>
            <p className="text-sm text-gray-600">Sent First Message</p>
          </div>
          <div className="p-4 bg-gray-100 rounded-xl text-center">
            <p className="text-3xl font-bold text-gray-700">{activation?.total_activated || 0}</p>
            <p className="text-sm text-gray-600">Total Activated Users</p>
          </div>
        </div>
      </div>

      {/* Retention Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-500" />
          Retention - User Engagement Over Time
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* DAU/WAU/MAU */}
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Active Users</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                <span className="text-gray-700">DAU (Daily)</span>
                <span className="text-xl font-bold text-purple-600">{retention?.dau || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                <span className="text-gray-700">WAU (Weekly)</span>
                <span className="text-xl font-bold text-blue-600">{retention?.wau || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                <span className="text-gray-700">MAU (Monthly)</span>
                <span className="text-xl font-bold text-green-600">{retention?.mau || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#FAB710]/10 rounded-xl border-2 border-[#FAB710]/30">
                <span className="text-gray-700">DAU/MAU Stickiness</span>
                <span className="text-xl font-bold text-[#FAB710]">{retention?.stickiness || 0}%</span>
              </div>
            </div>
          </div>

          {/* Cohort Retention */}
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Cohort Retention (Weekly)</h3>
            <div className="space-y-2">
              {retention?.cohort_retention?.map((cohort, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-xl">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-700">{cohort.cohort}</span>
                    <span className="text-sm font-bold text-gray-700">{cohort.retention_rate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${cohort.retention_rate >= 50 ? 'bg-green-500' : cohort.retention_rate >= 30 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${cohort.retention_rate}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{cohort.retained} of {cohort.total} users</p>
                </div>
              ))}
            </div>
            
            {/* Churn */}
            <div className="mt-4 p-4 bg-red-50 rounded-xl">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-red-800">Churn Rate</p>
                  <p className="text-xs text-red-600">{retention?.churned_users || 0} churned users</p>
                </div>
                <span className="text-2xl font-bold text-red-600">{retention?.churn_rate || 0}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-500" />
          Revenue - Monetization Metrics
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Key Revenue Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-emerald-50 rounded-xl text-center border-2 border-emerald-200">
              <p className="text-3xl font-bold text-emerald-600">${revenue?.mrr || 0}</p>
              <p className="text-sm text-gray-600">MRR</p>
              <p className="text-xs text-gray-400">Monthly Recurring Revenue</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-blue-600">${revenue?.arr || 0}</p>
              <p className="text-sm text-gray-600">ARR</p>
              <p className="text-xs text-gray-400">Annual Recurring Revenue</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-purple-600">${revenue?.arpu || 0}</p>
              <p className="text-sm text-gray-600">ARPU</p>
              <p className="text-xs text-gray-400">Avg Revenue Per User</p>
            </div>
            <div className="p-4 bg-pink-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-pink-600">${revenue?.arppu || 0}</p>
              <p className="text-sm text-gray-600">ARPPU</p>
              <p className="text-xs text-gray-400">Avg Revenue Per Paying User</p>
            </div>
          </div>

          {/* LTV & CAC */}
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Unit Economics</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                <div>
                  <span className="font-medium text-gray-700">LTV</span>
                  <p className="text-xs text-gray-500">Customer Lifetime Value</p>
                </div>
                <span className="text-2xl font-bold text-green-600">${revenue?.ltv || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                <div>
                  <span className="font-medium text-gray-700">CAC</span>
                  <p className="text-xs text-gray-500">Customer Acquisition Cost</p>
                </div>
                <span className="text-2xl font-bold text-red-600">${revenue?.estimated_cac || 0}</span>
              </div>
              <div className={`flex items-center justify-between p-3 rounded-xl border-2 ${
                revenue?.ltv_cac_ratio >= 3 ? 'bg-green-100 border-green-300' : 
                revenue?.ltv_cac_ratio >= 1 ? 'bg-yellow-100 border-yellow-300' : 
                'bg-red-100 border-red-300'
              }`}>
                <div>
                  <span className="font-medium text-gray-700">LTV:CAC Ratio</span>
                  <p className="text-xs text-gray-500">{revenue?.ltv_cac_ratio >= 3 ? 'Healthy (3x+)' : revenue?.ltv_cac_ratio >= 1 ? 'Moderate' : 'Needs improvement'}</p>
                </div>
                <span className="text-2xl font-bold">{revenue?.ltv_cac_ratio || 0}x</span>
              </div>
            </div>
          </div>
        </div>

        {/* Conversion & Subscription Breakdown */}
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-700 mb-3">Conversion Rates</h3>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-700">Free → Paid</span>
                  <span className="font-bold text-green-600">{revenue?.conversion_rate || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(revenue?.conversion_rate || 0, 100)}%` }} />
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-700">Premium → Elite</span>
                  <span className="font-bold text-[#1C3A7A]">{revenue?.upgrade_rate || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#1C3A7A] h-2 rounded-full" style={{ width: `${Math.min(revenue?.upgrade_rate || 0, 100)}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-700 mb-3">Subscription Distribution</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1 text-center p-3 bg-gray-100 rounded-xl">
                <p className="text-2xl font-bold text-gray-600">{revenue?.free_users || 0}</p>
                <p className="text-xs text-gray-500">Free</p>
              </div>
              <div className="flex-1 text-center p-3 bg-[#FAB710]/20 rounded-xl">
                <p className="text-2xl font-bold text-[#FAB710]">{revenue?.premium_users || 0}</p>
                <p className="text-xs text-gray-500">Premium</p>
              </div>
              <div className="flex-1 text-center p-3 bg-[#1C3A7A]/10 rounded-xl">
                <p className="text-2xl font-bold text-[#1C3A7A]">{revenue?.elite_users || 0}</p>
                <p className="text-xs text-gray-500">Elite</p>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Trend */}
        <div className="mt-6">
          <h3 className="font-medium text-gray-700 mb-3">14-Day Revenue Trend</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(val) => `$${val}`} />
                <Tooltip formatter={(val) => [`$${val}`, 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.2} strokeWidth={2} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
